import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * Used to ignore stale animation completions when several cart updates happen quickly.
   * @type {number}
   */
  #animationToken = 0;

  /**
   * Last rendered cart count.
   * @type {number | null}
   */
  #lastRenderedCount = null;

  /** @type {number} */
  get currentCartCount() {
    const count = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isNaN(count) ? 0 : count;
  }

  set currentCartCount(value) {
    const normalizedValue = Number.isFinite(value) ? Math.max(0, value) : 0;
    const nextText = normalizedValue > 0 && normalizedValue < 100 ? String(normalizedValue) : '';

    if (this.refs.cartBubbleCount.textContent !== nextText) {
      this.refs.cartBubbleCount.textContent = nextText;
    }

    this.#lastRenderedCount = normalizedValue;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.#animationToken += 1;
  }

  /**
   * Handles the cart update event.
   *
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const itemCount = Number(event.detail.data?.itemCount ?? 0);
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(Number.isFinite(itemCount) ? itemCount : 0, comingFromProductForm);
  };

  /**
   * Safely stores cart count for quick cross-page correction.
   *
   * @param {number} value
   */
  #storeCartCount(value) {
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(value),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // no-op
    }
  }

  /**
   * Renders the cart bubble.
   *
   * @param {number} itemCount - The number of items in the cart or delta when coming from product form.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} [animate=true] - Whether the bubble should animate.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const normalizedItemCount = Number.isFinite(itemCount) ? Math.max(0, itemCount) : 0;
    const nextCount = comingFromProductForm ? this.currentCartCount + normalizedItemCount : normalizedItemCount;
    const hasCart = nextCount > 0;
    const shouldAnimate = hasCart && animate;

    if (this.#lastRenderedCount === nextCount && !shouldAnimate) return;

    this.refs.cartBubbleCount.classList.toggle('hidden', !hasCart);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasCart);
    this.classList.toggle('header-actions__cart-icon--has-cart', hasCart);

    this.currentCartCount = nextCount;
    this.#storeCartCount(this.currentCartCount);

    this.#animationToken += 1;
    const animationToken = this.#animationToken;

    if (!shouldAnimate) {
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
      return;
    }

    this.refs.cartBubble.classList.remove('cart-bubble--animating');

    requestAnimationFrame(() => {
      if (animationToken !== this.#animationToken) return;
      this.refs.cartBubble.classList.add('cart-bubble--animating');
    });

    await onAnimationEnd(this.refs.cartBubbleText);

    if (animationToken !== this.#animationToken) return;

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    let sessionStorageCount = null;

    try {
      sessionStorageCount = sessionStorage.getItem('cart-count');
    } catch (_) {
      return;
    }

    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent ?? '';

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (String(value) === visibleCount) return;
      if (Date.now() - Number(timestamp) >= 10000) return;

      const count = parseInt(value, 10);

      if (Number.isFinite(count) && count >= 0) {
        this.renderCartBubble(count, false, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}